/**
 * 12-Week First Mile (Beginner Endurance)
 *
 * For users who pick the `endurance` goal at onboarding AND tag
 * themselves as `beginner`. The intermediate Endurance Base assumes the
 * user can already run 30+ minutes continuously at conversational pace.
 * A true beginner often can't run for 5 minutes without stopping.
 *
 * Methodology — Walk-to-Run progression, modeled on the NHS Couch-to-5K
 * (the gold-standard beginner running program with millions of finishers
 * across two decades). The original C25K is 9 weeks; this extends it to
 * 12 weeks with a cutback week and a more conservative ramp so users
 * who haven't run since high school don't get hurt.
 *
 * Structure:
 * - 4 days/week: 3 walk-run sessions + 1 strength resilience day
 * - W1: walk-run intervals starting at 60s run / 90s walk × 8 (~20 min)
 * - W2-5: progressively longer run intervals, shorter walk recovery
 * - W6: cutback / consolidation week
 * - W7-10: build to continuous running (10-20 min sustained)
 * - W11: bridge to 5K — 25-30 min continuous
 * - W12: 5K time trial
 *
 * The strength day is non-negotiable for beginners. Soft tissue (calves,
 * shins, plantar fascia) needs months to adapt to running impact. The
 * strength session gives connective tissue a load stimulus on a non-run
 * day, which is what the running literature credits with reducing
 * shin-splint and runner's-knee incidence in novices.
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'first-mile-12week',
  name: '12-Week First Mile',
  description: 'Walk-to-run progression for true beginners. Start at 60-second running intervals, finish at a continuous 5K. Includes a runner-resilience strength day to keep shins and calves healthy as the volume grows.',
  level: 'recruit',
  duration: 12,
  daysPerWeek: 4,
  goals: ['endurance'],
  difficulty: 'beginner',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false,
};

const periodization: ProgramPeriodizationModel = {
  style: 'block',
  phases: [
    // W1 + W12: Testing
    {
      weeks: [1, 12],
      type: 'assessment',
      name: 'Endurance Testing',
      focus: 'W1 baseline (1-mile walk for time + how long you can run before stopping); W12 5K time trial',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
    },
    // Weeks 2-5: Walk-Run Foundation. Run intervals grow from 90s to
    // 5 min; walk recoveries stay generous. Volume builds slowly so
    // tissues adapt without breaking.
    {
      weeks: [2, 3, 4, 5],
      type: 'accumulation',
      name: 'Walk-Run Foundation',
      focus: 'Run intervals grow from 90s to 5 min; walk recoveries stay long.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.55,
        endIntensity: 0.65,
        weeklyIncrease: 0.025,
      },
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 1.0,
        restPeriod: '90s',
      },
    },
    // Week 6: Consolidation. Volume drops, intensity holds. Lets
    // tendons and bones catch up before the bigger jump in W7-10.
    {
      weeks: [6],
      type: 'deload',
      name: 'Consolidation Week',
      focus: 'Half the volume, keep the routine. Tissue catch-up.',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.6,
      progression: {
        type: 'linear',
        startIntensity: 0.60,
        endIntensity: 0.60,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [6, 8],
        setsMultiplier: 0.6,
        restPeriod: '90s',
      },
    },
    // Weeks 7-10: Continuous Running. Walk breaks shrink to almost
    // nothing. By W10 the user is doing a single 20+ min easy run.
    {
      weeks: [7, 8, 9, 10],
      type: 'accumulation',
      name: 'Continuous Running',
      focus: 'Bridge from intervals to a continuous 20+ min easy run.',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.72,
        weeklyIncrease: 0.0175,
      },
      trainingParameters: {
        repRange: [4, 6],
        setsMultiplier: 1.0,
        restPeriod: '60s',
      },
    },
    // Week 11: 5K Bridge. The first 25-30 min sessions. One long run
    // earlier in the week, lighter sessions setting up the W12 test.
    {
      weeks: [11],
      type: 'realization',
      name: '5K Bridge',
      focus: 'First 25-30 min continuous runs. Last week before the test.',
      intensityMultiplier: 0.95,
      volumeMultiplier: 0.85,
      progression: {
        type: 'linear',
        startIntensity: 0.72,
        endIntensity: 0.72,
        weeklyIncrease: 0,
      },
      trainingParameters: {
        repRange: [3, 4],
        setsMultiplier: 0.85,
        restPeriod: '60s',
      },
    },
  ],
};

// BASE TEMPLATES

const testingDay: WorkoutTemplateDay = {
  name: 'Endurance Test',
  sessionType: 'assessment',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Open hips before any running' },
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Very easy walk-jog to raise heart rate' },
      ],
    },
    {
      name: 'Time Trial',
      type: 'testing',
      exercises: [
        { name: 'Run', duration: '1 mile', format: 'cardio', intensity: 'Hard', details: 'W1: walk it for time. W12: run 5K (3.1 mi) for time. Either way, record minutes.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Walk to bring heart rate down' },
        { name: 'Hip Mobility Flow', sets: 2, reps: 8, format: 'recovery', details: 'Stretch hips and calves' },
      ],
    },
  ],
};

// Day 1, 3, 5 — Walk-Run intervals. The duration field describes the
// total session; details spell out the exact ratio of run to walk per
// the periodization phase the user is in.
const walkRunDay: WorkoutTemplateDay = {
  name: 'Walk-Run Session',
  sessionType: 'pt',
  estimatedDuration: 30,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Hip Opener Flow', sets: 1, reps: 8, format: 'recovery', details: 'Get the hips moving before running' },
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Brisk walk to warm up. Don\'t skip — cold legs hurt.' },
      ],
    },
    {
      name: 'Walk-Run Intervals',
      type: 'conditioning',
      format: 'intervals',
      exercises: [
        { name: 'Interval Run', sets: 8, reps: '60-90s run / 90s walk', format: 'intervals', details: 'Phase 1 (W2-5): 60-90s run, 90s walk × 8. Phase 2 (W6): cut to 6 sets. Phase 3 (W7-10): build to 5-10 min run / 60s walk × 4. Phase 4 (W11): 25-30 min continuous easy run, no walk break.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Walk to bring heart rate down — recovery starts here' },
        { name: 'Hip Mobility Flow', sets: 1, reps: 10, format: 'recovery', details: 'Stretch hips, calves, and glutes' },
      ],
    },
  ],
};

// Day 2 — Strength resilience. Calves, glutes, hips, core. The running
// literature credits this kind of cross-training with cutting novice
// running injuries roughly in half.
const resilienceDay: WorkoutTemplateDay = {
  name: 'Runner Resilience',
  sessionType: 'pt',
  estimatedDuration: 35,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Cat-Cow Stretch', sets: 2, reps: 8, format: 'recovery', details: 'Spine mobility' },
        { name: 'Hip Opener Flow', sets: 1, reps: 10, format: 'recovery', details: 'Hips before single-leg work' },
      ],
    },
    {
      name: 'Strength + Stability',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 3, reps: 10, intensity: 'RPE 6', details: 'Light dumbbell or kettlebell. Pattern practice, not loading.' },
        { name: 'Hip Thrust', sets: 3, reps: 12, intensity: 'RPE 6', details: 'Bodyweight or light load. Strong glutes prevent runner\'s knee.' },
        { name: 'Calf Raise', sets: 3, reps: 15, intensity: 'RPE 7', details: 'Slow tempo, full range. Seated (bent-knee) hits the soleus, the endurance calf.' },
        { name: 'Plank Hold', sets: 3, reps: '30s', format: 'bodyweight', details: 'Anti-extension. Core stability prevents wobble at mile 2.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hip Mobility Flow', sets: 2, reps: 10, format: 'recovery', details: 'Open the hips and decompress' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const firstMile12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W12)
    'testing-week-day1': testingDay,
    'testing-week-day2': restDay,
    'testing-week-day3': restDay,
    'testing-week-day4': restDay,
    'testing-week-day5': restDay,
    'testing-week-day6': restDay,
    'testing-week-day7': restDay,

    // Training cycle — Mon/Wed/Fri runs, Tuesday strength resilience
    'training-day1': walkRunDay,
    'training-day2': resilienceDay,
    'training-day3': walkRunDay,
    'training-day4': restDay,
    'training-day5': walkRunDay,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};
